'use client';

import useEmblaCarousel from 'embla-carousel-react';
import {useRouter} from 'next/navigation';
import {type FC, memo, type ReactElement, useCallback, useContext, useEffect, useMemo} from 'react';
import {StorageImageNew} from '@/components/atoms/StorageImage';
import type {Celebrity} from '@/entities/Celebrity/type/celebrities';
// import Autoplay from 'embla-carousel-autoplay';
import ShowcaseContextNew from './ShowcaseContextNew';

interface CarouselMobileProps {
  celebrities: Celebrity[];
}

interface CelebrityCardProps {
  celebrity: Celebrity;
  isPriority: boolean;
  onClick: () => void;
}

const CelebrityCard: FC<CelebrityCardProps> = memo(
  ({celebrity, isPriority, onClick}) => {
    const {first_name, last_name, main_screen_img} = celebrity;

    return (
      <div className="min-w-0 flex-[0_0_auto] px-1">
        <button
          className="relative flex h-[312px] w-[240px] shrink-0"
          onClick={onClick}
          aria-label={`Перейти к профилю ${first_name} ${last_name}`}
        >
          {main_screen_img && (
            <StorageImageNew
              src={main_screen_img}
              alt={`${first_name} ${last_name}`}
              className="h-full w-full rounded-lg object-cover"
              withBlur
              priority={isPriority}
              aspectRatio="240/312"
              sizes="240px"
              format="webp"
              quality={85}
              resize="cover"
              sources={[
                {
                  media: '(min-width: 1280px)',
                  width: 240,
                  height: 312,
                  resize: 'cover',
                  dprs: [1, 2, 3],
                  quality: 85,
                },
                {
                  media: '(max-width: 768px)',
                  width: 240,
                  height: 312,
                  resize: 'cover',
                  dprs: [1, 2],
                  quality: 80,
                },
              ]}
            />
          )}
        </button>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.celebrity.id === nextProps.celebrity.id && prevProps.isPriority === nextProps.isPriority;
  },
);

CelebrityCard.displayName = 'CelebrityCard';

export const CarouselMobileNew: FC<CarouselMobileProps> = memo(
  ({celebrities}): ReactElement | null => {
    const {activeIdx, setActiveIdx} = useContext(ShowcaseContextNew);
    const router = useRouter();

    const PRIORITY_COUNT = 3;

    const emblaOptions = useMemo(
      () => ({
        align: 'center' as const,
        containScroll: 'trimSnaps' as const,
        skipSnaps: false,
        dragFree: false,
        loop: true,
      }),
      [],
    );

    const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

    const onSelect = useCallback(() => {
      if (!emblaApi) return;
      const selectedIndex = emblaApi.selectedScrollSnap();
      setActiveIdx(selectedIndex);
    }, [emblaApi, setActiveIdx]);

    useEffect(() => {
      if (!emblaApi) return;
      onSelect();
      emblaApi.on('select', onSelect);
      emblaApi.on('reInit', onSelect);

      return () => {
        emblaApi.off('select', onSelect);
        emblaApi.off('reInit', onSelect);
      };
    }, [emblaApi, onSelect]);

    useEffect(() => {
      if (!emblaApi) return;
      const currentIndex = emblaApi.selectedScrollSnap();
      if (currentIndex !== activeIdx) {
        emblaApi.scrollTo(activeIdx);
      }
    }, [activeIdx, emblaApi]);

    const createNavigationHandler = useCallback(
      (id: number) => () => {
        router.push(`/partners/${id}`);
      },
      [router],
    );

    const celebrityCards = useMemo(
      () =>
        celebrities.map((celebrity, idx) => {
          const isNearActive = Math.abs(idx - activeIdx) <= 1;
          const isInInitialView = idx < PRIORITY_COUNT;
          const isPriority = isInInitialView || isNearActive;

          return (
            <CelebrityCard
              key={celebrity.id}
              celebrity={celebrity}
              isPriority={isPriority}
              onClick={createNavigationHandler(celebrity.id)}
            />
          );
        }),
      [celebrities, activeIdx, createNavigationHandler],
    );

    return (
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex items-stretch">{celebrityCards}</div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.celebrities.length !== nextProps.celebrities.length) {
      return false;
    }

    for (let i = 0; i < prevProps.celebrities.length; i++) {
      if (prevProps.celebrities[i].id !== nextProps.celebrities[i].id) {
        return false;
      }
    }

    return true;
  },
);

CarouselMobileNew.displayName = 'CarouselMobileNew';
